import React from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Storage as StorageIcon,
    Analytics as AnalyticsIcon,
    Speed as SpeedIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    TrendingUp as TrendingUpIcon,
    Assessment as AssessmentIcon,
    CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';

const MetricCard = ({ title, value, subtitle, icon, color = 'primary', loading = false }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: `${color}.light`,
                    color: `${color}.contrastText`,
                    mr: 2
                }}>
                    {icon}
                </Box>
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    {title}
                </Typography>
            </Box>

            {loading ? (
                <LinearProgress sx={{ mb: 2 }} />
            ) : (
                <Typography variant="h3" component="div" color={`${color}.main`} sx={{ mb: 1 }}>
                    {value}
                </Typography>
            )}

            {subtitle && (
                <Typography variant="body2" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const StatusIndicator = ({ status, label }) => {
    const getStatusProps = (status) => {
        switch (status) {
            case 'healthy':
            case 'connected':
            case 'available':
                return { color: 'success', icon: <CheckCircleIcon /> };
            case 'error':
            case 'disconnected':
            case 'unavailable':
                return { color: 'error', icon: <ErrorIcon /> };
            case 'running':
            case 'processing':
                return { color: 'info', icon: <ScheduleIcon /> };
            default:
                return { color: 'default', icon: <ScheduleIcon /> };
        }
    };

    const { color, icon } = getStatusProps(status);

    return (
        <Chip
            icon={icon}
            label={label}
            color={color}
            variant="outlined"
            size="small"
        />
    );
};

const Dashboard = () => {
    const { state, apiActions } = useApp();
    const { systemStatus, facilities, runningAnalyses, apiAvailable, analysisTypes, notifications } = state;

    const handleRefresh = async () => {
        await Promise.all([
            apiActions.fetchSystemStatus(),
            apiActions.fetchFacilities(),
            apiActions.fetchRunningAnalyses(),
        ]);
    };

    // Calculate metrics
    const totalFacilities = systemStatus?.total_facilities || 0;
    const runningAnalysesCount = systemStatus?.running_analyses || 0;
    const pricingAnalysesCount = systemStatus?.analysis_counts?.pricing || 0;
    const descriptionAnalysesCount = systemStatus?.analysis_counts?.description || 0;

    const scrapedFacilities = facilities.filter(f => f.scraped_at).length;
    const scrapingProgress = totalFacilities > 0 ? (scrapedFacilities / totalFacilities) * 100 : 0;

    const totalAnalysesCompleted = pricingAnalysesCount + descriptionAnalysesCount;
    const analysisProgress = totalFacilities > 0 ? (totalAnalysesCompleted / (totalFacilities * analysisTypes.length)) * 100 : 0;

    // Recent activity from notifications
    const recentActivity = notifications.slice(0, 5);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    KiddieScraping Dashboard
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <StatusIndicator
                        status={apiAvailable ? 'connected' : 'disconnected'}
                        label={apiAvailable ? 'API Connected' : 'API Disconnected'}
                    />
                    <Tooltip title="Refresh Data">
                        <IconButton onClick={handleRefresh} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* API Status Alert */}
            {!apiAvailable && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    API server is not available. Please ensure the API server URL is set correctly.
                </Alert>
            )}

            {/* Main Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Total Facilities"
                        value={totalFacilities}
                        subtitle="Facilities in database"
                        icon={<StorageIcon />}
                        color="primary"
                        loading={!systemStatus}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Scraped Facilities"
                        value={scrapedFacilities}
                        subtitle={`${scrapingProgress.toFixed(1)}% complete`}
                        icon={<CloudDownloadIcon />}
                        color="info"
                        loading={!systemStatus}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Running Analyses"
                        value={runningAnalysesCount}
                        subtitle="Currently processing"
                        icon={<SpeedIcon />}
                        color="warning"
                        loading={!systemStatus}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Completed Analyses"
                        value={totalAnalysesCompleted}
                        subtitle={`${analysisProgress.toFixed(1)}% of all possible`}
                        icon={<AnalyticsIcon />}
                        color="success"
                        loading={!systemStatus}
                    />
                </Grid>
            </Grid>

            {/* Progress Bars */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Scraping Progress
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={scrapingProgress}
                                        color="info"
                                        sx={{ height: 8 }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {`${Math.round(scrapingProgress)}%`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {scrapedFacilities} of {totalFacilities} facilities scraped
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Analysis Progress
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={analysisProgress}
                                        color="success"
                                        sx={{ height: 8 }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {`${Math.round(analysisProgress)}%`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {totalAnalysesCompleted} of {totalFacilities * analysisTypes.length} possible analyses
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Analysis Breakdown */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <AssessmentIcon sx={{ mr: 1 }} />
                                Analysis Breakdown
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h4" color="primary.main">
                                            {pricingAnalysesCount}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Pricing Analyses
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h4" color="secondary.main">
                                            {descriptionAnalysesCount}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Description Analyses
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <TrendingUpIcon sx={{ mr: 1 }} />
                                System Performance
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h4" color="success.main">
                                            {Object.keys(runningAnalyses).length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Active Tasks
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h4" color="info.main">
                                            {analysisTypes.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Analysis Types
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Recent Activity */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Recent Activity
                        </Typography>
                        {recentActivity.length > 0 ? (
                            <List dense>
                                {recentActivity.map((notification, index) => (
                                    <React.Fragment key={notification.id}>
                                        <ListItem>
                                            <ListItemIcon>
                                                {notification.type === 'success' && <CheckCircleIcon color="success" />}
                                                {notification.type === 'error' && <ErrorIcon color="error" />}
                                                {notification.type === 'info' && <ScheduleIcon color="info" />}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={notification.message}
                                                secondary={new Date(notification.timestamp).toLocaleTimeString()}
                                            />
                                        </ListItem>
                                        {index < recentActivity.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No recent activity
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Running Analyses
                        </Typography>
                        {Object.keys(runningAnalyses).length > 0 ? (
                            <List dense>
                                {Object.entries(runningAnalyses).map(([key, analysis]) => {
                                    const [facilityId, analysisType] = key.split('_');
                                    return (
                                        <ListItem key={key}>
                                            <ListItemIcon>
                                                <ScheduleIcon color="info" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${analysisType} analysis`}
                                                secondary={`Facility ${facilityId} - Started ${new Date(analysis.start_time).toLocaleTimeString()}`}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No analyses currently running
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard; 